import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  Cr02DialogComponent,
  Cr02DialogConfig,
  Cr02DualPromptResult
} from './cr02-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class Cr02DialogService {
  constructor(private modalService: NgbModal) {}

  private open(config: Cr02DialogConfig) {
    const ref = this.modalService.open(Cr02DialogComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      windowClass: 'cr02-ngb-dialog'
    });
    ref.componentInstance.applyConfig(config);
    return ref;
  }

  alert(config: Omit<Cr02DialogConfig, 'mode'>): Promise<void> {
    return this.open({
      ...config,
      mode: 'alert',
      type: config.type || 'info',
      confirmText: config.confirmText || 'OK'
    }).result.then(() => undefined, () => undefined);
  }

  success(title: string, message?: string): Promise<void> {
    return this.alert({ title, message, type: 'success' });
  }

  error(title: string, message?: string): Promise<void> {
    return this.alert({ title, message, type: 'error' });
  }

  warning(title: string, message?: string): Promise<void> {
    return this.alert({ title, message, type: 'warning' });
  }

  confirm(config: Omit<Cr02DialogConfig, 'mode'>): Promise<boolean> {
    return this.open({
      ...config,
      mode: 'confirm',
      type: config.type || 'warning',
      confirmText: config.confirmText || 'Confirm',
      cancelText: config.cancelText || 'Cancel'
    }).result.then(() => true, () => false);
  }

  prompt(config: Omit<Cr02DialogConfig, 'mode'>): Promise<string | null> {
    return this.open({
      ...config,
      mode: 'prompt',
      type: config.type || 'info',
      confirmText: config.confirmText || 'Continue',
      cancelText: config.cancelText || 'Cancel'
    }).result.then((value) => String(value ?? ''), () => null);
  }

  /** Prompt for two values (e.g. column name + extracted sample value). */
  promptDual(config: Omit<Cr02DialogConfig, 'mode'>): Promise<Cr02DualPromptResult | null> {
    return this.open({
      ...config,
      mode: 'prompt-dual',
      type: config.type || 'info',
      confirmText: config.confirmText || 'Add',
      cancelText: config.cancelText || 'Cancel'
    }).result.then(
      (value) => (value && typeof value === 'object' ? value as Cr02DualPromptResult : null),
      () => null
    );
  }
}
